import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Loader2, RefreshCw, Copy, CheckCircle2, XCircle, AlertCircle, Rocket, Database, KeyRound, Send, FileText, Sparkles, IndianRupee, UserCheck, Mail, Baby, Eye, Video, Trash2, CalendarDays, Phone, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { V25SmokeCard } from '../../components/admin/V25SmokeCard';
import { PushStoreReadinessCard } from '../../components/admin/PushStoreReadinessCard';
import { StoreReviewAccountCard } from '../../components/admin/StoreReviewAccountCard';
import { DuplicateAccountsCard } from '../../components/admin/DuplicateAccountsCard';
import { SlowRequestsCard } from '../../components/admin/SlowRequestsCard';

const StatusIcon = ({ ok }) => ok
  ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" data-testid="status-ok" />
  : <XCircle className="h-5 w-5 text-red-500 shrink-0" data-testid="status-fail" />;

/* ═══════════ V14.7 — Bunny Pipeline Tools ═══════════ */
function BunnyPipelineTools({ running, setRunning }) {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [importResult, setImportResult] = useState(null);
  const [terminateResult, setTerminateResult] = useState(null);

  const handleTerminate = async () => {
    if (!window.confirm('Terminate ALL stuck pipeline jobs (downloading/uploading)? This marks them as failed so they can be retried.')) return;
    setRunning(r => ({ ...r, terminateJobs: true }));
    setTerminateResult(null);
    try {
      const res = await api.post('/api/bunny/terminate-stuck');
      setTerminateResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setRunning(r => ({ ...r, terminateJobs: false }));
  };

  const handleBulkImport = async () => {
    if (!dateFrom || !dateTo) { toast.error('Select both dates'); return; }
    setRunning(r => ({ ...r, bulkImport: true }));
    setImportResult(null);
    try {
      const res = await api.post(`/api/bunny/bulk-import-recordings?date_from=${dateFrom}&date_to=${dateTo}`);
      setImportResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
    setRunning(r => ({ ...r, bulkImport: false }));
  };

  return (
    <Card data-testid="bunny-pipeline-tools-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="h-5 w-5 text-rose-500" />
          Bunny Recording Pipeline
        </CardTitle>
        <CardDescription>
          Terminate stuck jobs or bulk-import past session recordings from Zoom → Bunny.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Terminate stuck */}
        <div className="flex items-center gap-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={handleTerminate}
            disabled={running.terminateJobs}
            data-testid="terminate-stuck-btn"
          >
            {running.terminateJobs ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Terminate Stuck Jobs
          </Button>
          <span className="text-xs text-muted-foreground">Marks all downloading/uploading pipeline jobs as failed</span>
        </div>
        {terminateResult && (
          <div className="text-xs bg-muted/50 rounded p-2">
            {terminateResult.message}
          </div>
        )}

        <Separator />

        {/* Bulk import */}
        <div className="space-y-2">
          <p className="text-sm font-medium flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Bulk Import Past Recordings
          </p>
          <p className="text-xs text-muted-foreground">
            Fetches recordings from Zoom for all completed sessions in the date range and triggers the Bunny upload pipeline.
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-40" data-testid="bulk-import-date-from" />
            <span className="text-sm text-muted-foreground">to</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-40" data-testid="bulk-import-date-to" />
            <Button
              size="sm"
              onClick={handleBulkImport}
              disabled={running.bulkImport}
              data-testid="bulk-import-btn"
            >
              {running.bulkImport ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Video className="h-4 w-4 mr-2" />}
              Import Recordings
            </Button>
          </div>
        </div>
        {importResult && (
          <div className="text-xs bg-muted/50 rounded p-2 space-y-1">
            <p className="font-medium">{importResult.message}</p>
            {importResult.sessions_triggered > 0 && (
              <p>Sessions queued: {importResult.sessions_triggered} | Skipped (already ready): {importResult.sessions_skipped}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function LaunchReadinessPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState({}); // per-button loading state
  const [opsDiag, setOpsDiag] = useState(null);
  // V14.5 Part 19n — Email transport diagnostics (Resend)
  const [emailDiag, setEmailDiag] = useState(null);
  const [emailTestTo, setEmailTestTo] = useState('ankit@soulsanskar.com');
  const [emailTestResult, setEmailTestResult] = useState(null);
  // V14.5 Part 19t — Learn tab visibility diagnostic
  const [learnDiagEmail, setLearnDiagEmail] = useState('');
  const [learnDiagResult, setLearnDiagResult] = useState(null);
  const [perfProbe, setPerfProbe] = useState(null);


  const refresh = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/ops/launch-readiness');
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load status');
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const runAction = async (key, fn, successMsg) => {
    setRunning(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fn();
      toast.success(successMsg || 'Done');
      // Re-fetch the dashboard so green/red dots update
      await refresh();
      return res;
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Action failed');
    } finally {
      setRunning(prev => ({ ...prev, [key]: false }));
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  // V14.5 Part 19n — Email Transport Diagnostics renderer (independent of
  // launch-readiness API so it works even when that endpoint is broken).
  const renderEmailDiagCard = () => (
    <Card className="border-amber-500/40 bg-amber-500/5" data-testid="email-transport-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Email Transport (Resend) — Production Diagnostics
        </CardTitle>
        <CardDescription>
          Tests whether the running container can actually send email via Resend.
          Use this when "Check your email" succeeds in the UI but no email arrives.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={async () => {
              setRunning(p => ({ ...p, emailDiag: true }));
              try {
                const res = await api.get('/api/admin/diagnostics/email-transport');
                setEmailDiag(res.data);
              } catch (err) {
                toast.error(err.response?.data?.detail || err.message || 'Failed');
              } finally {
                setRunning(p => ({ ...p, emailDiag: false }));
              }
            }}
            disabled={running.emailDiag}
            variant="outline"
            data-testid="email-diag-check-btn"
          >
            {running.emailDiag ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
            Check env vars
          </Button>
        </div>

        {emailDiag && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm" data-testid="email-diag-output">
            <div className="font-semibold flex flex-wrap items-center gap-2">
              Process env snapshot
              {emailDiag.process_env?.RESEND_API_KEY_present
                ? <Badge variant="default" className="bg-emerald-500">RESEND key loaded</Badge>
                : <Badge variant="destructive">RESEND key MISSING</Badge>
              }
              {emailDiag.email_service_module?.RESEND_API_KEY_loaded_at_import
                ? <Badge variant="outline" className="border-emerald-500 text-emerald-600">key loaded at import</Badge>
                : <Badge variant="outline" className="border-amber-500 text-amber-600">key NOT at import — race condition</Badge>
              }
              {String(emailDiag.process_env?.EMAILS_ENABLED || 'true').toLowerCase() === 'false' && (
                <Badge variant="destructive" data-testid="emails-disabled-badge">
                  EMAILS_ENABLED=false → kill-switch ON
                </Badge>
              )}
            </div>
            {String(emailDiag.process_env?.EMAILS_ENABLED || 'true').toLowerCase() === 'false' && (
              <div className="rounded-md border border-red-500/50 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-300" data-testid="kill-switch-warning">
                <strong>🛑 Email kill-switch is ON.</strong> Every outbound email is being silently swallowed.
                Go to your Emergent production env vars and either set <code className="bg-background px-1 rounded">EMAILS_ENABLED=true</code> or
                remove the variable entirely (default is true), then redeploy.
              </div>
            )}
            <pre className="overflow-x-auto bg-background rounded p-3 text-xs">
{JSON.stringify(emailDiag, null, 2)}
            </pre>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">RESEND_API_KEY_present</span>: confirms Emergent has injected the key into the running container.<br/>
              <span className="font-medium">RESEND_API_KEY_loaded_at_import</span>: false means Emergent injected the key AFTER Python started — runtime self-heal patch should still recover it.
            </p>
          </div>
        )}

        <Separator />

        <div className="space-y-2">
          <p className="text-sm font-medium">Send a real test email through this environment&apos;s transport:</p>
          <div className="flex flex-wrap gap-2">
            <Input
              type="email"
              value={emailTestTo}
              onChange={(e) => setEmailTestTo(e.target.value)}
              placeholder="recipient@example.com"
              className="max-w-xs"
              data-testid="email-test-to-input"
            />
            <Button
              onClick={async () => {
                if (!emailTestTo || !emailTestTo.includes('@')) {
                  toast.error('Enter a valid recipient email');
                  return;
                }
                setRunning(p => ({ ...p, emailTest: true }));
                setEmailTestResult(null);
                try {
                  const res = await api.post('/api/admin/diagnostics/email-test', { to: emailTestTo });
                  setEmailTestResult(res.data);
                  if (res.data?.sent) {
                    toast.success(`Test email dispatched to ${emailTestTo}`);
                  } else {
                    toast.error('Send returned false — see details below');
                  }
                } catch (err) {
                  setEmailTestResult({ error: err.response?.data || err.message });
                  toast.error('Test send failed — see details below');
                } finally {
                  setRunning(p => ({ ...p, emailTest: false }));
                }
              }}
              disabled={running.emailTest}
              data-testid="email-test-send-btn"
            >
              {running.emailTest ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send test email
            </Button>
          </div>

          {emailTestResult && (
            <div
              className={`rounded-lg border p-4 space-y-2 text-sm ${
                emailTestResult.sent
                  ? 'border-emerald-500/50 bg-emerald-500/5'
                  : 'border-red-500/50 bg-red-500/5'
              }`}
              data-testid="email-test-output"
            >
              <div className="font-semibold flex items-center gap-2">
                {emailTestResult.sent
                  ? <><CheckCircle2 className="h-5 w-5 text-emerald-500" /> Sent to {emailTestTo}</>
                  : <><XCircle className="h-5 w-5 text-red-500" /> Send failed</>
                }
                {emailTestResult.snapshot?.healed_at_runtime && (
                  <Badge variant="outline" className="border-amber-500 text-amber-600">
                    key healed at runtime
                  </Badge>
                )}
              </div>
              <pre className="overflow-x-auto bg-background rounded p-3 text-xs">
{JSON.stringify(emailTestResult, null, 2)}
              </pre>
              {emailTestResult.exception && (
                <p className="text-xs text-red-600">
                  Exception: <span className="font-mono">{emailTestResult.exception}</span>
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );


  // V14.5 Part 19z3 — Discovery audience + learner_type_target migration
  // (Founder-asked UI button replacing the CLI script
  //  `scripts/v19u_migrate_discovery_audience_and_tier.py`).
  //
  // Normalises legacy `discovery.listed_in_audiences` entries — strips
  // "ttc"/"elite"/"trial-only" tags, moves them onto the new
  // `discovery.learner_type_target` axis, defaults missing rows to "all"
  // (least-restrictive). Idempotent + writes a single audit row to
  // `discovery_migration_audit_col`.
  // V16.5 — Restore hard-deleted instructors (fixes "Unknown" payout rows).
  // Rebuilds archived user docs (same _id) from audit_logs / session
  // snapshots so all historic payout + session references resolve names.
  const renderPerfProbeCard = () => (
    <Card className="border-sky-500/40 bg-sky-500/5" data-testid="perf-probe-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="h-5 w-5" />
          Speed Test — CPU or Database?
        </CardTitle>
        <CardDescription>
          Measures, inside the server, how long one database round-trip takes and what
          100 queries cost. Run this on production when pages feel slow — it tells us
          whether more vCPU would help or whether the database round-trips are the cost.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          onClick={async () => {
            setRunning(p => ({ ...p, perfProbe: true }));
            setPerfProbe(null);
            try {
              const res = await api.get('/api/admin/ops/perf-probe');
              setPerfProbe(res.data);
              toast.success('Speed test complete');
            } catch (err) {
              toast.error(err.response?.data?.detail || err.message || 'Speed test failed');
            } finally {
              setRunning(p => ({ ...p, perfProbe: false }));
            }
          }}
          disabled={running.perfProbe}
          variant="outline"
          data-testid="perf-probe-run-btn"
        >
          {running.perfProbe ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gauge className="h-4 w-4 mr-2" />}
          Run speed test
        </Button>

        {perfProbe && (
          <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-2" data-testid="perf-probe-result">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <div className="text-muted-foreground">DB round-trip</div>
                <div className="font-mono text-base">{perfProbe.mongo_rtt_ms?.avg} ms</div>
                <div className="text-muted-foreground">p95 {perfProbe.mongo_rtt_ms?.p95} ms</div>
              </div>
              <div>
                <div className="text-muted-foreground">100 queries</div>
                <div className="font-mono text-base">{perfProbe.serial_100_queries_ms} ms</div>
              </div>
              <div>
                <div className="text-muted-foreground">CPU burn</div>
                <div className="font-mono text-base">{perfProbe.cpu_busy_ms} ms</div>
                <div className="text-muted-foreground">load {perfProbe.load_avg_1m} · {perfProbe.cpu_count} cpu</div>
              </div>
              <div>
                <div className="text-muted-foreground">Data / Index</div>
                <div className="font-mono text-base">{perfProbe.db?.data_mb} / {perfProbe.db?.index_mb} MB</div>
                <div className="text-muted-foreground">{perfProbe.db?.objects} docs</div>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground border-t pt-2">{perfProbe.verdict}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const renderLearnTabDiagCard = () => (
    <Card className="border-blue-500/40 bg-blue-500/5" data-testid="learn-tab-diag-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Eye className="h-5 w-5" />
          Learn Tab — Visibility Diagnostic
        </CardTitle>
        <CardDescription>
          See exactly which published courses a learner can/can&apos;t see in
          their Learn tab — and the precise reason if hidden.
          Useful for &quot;why doesn&apos;t this learner see my course?&quot; tickets.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            type="email"
            value={learnDiagEmail}
            onChange={(e) => setLearnDiagEmail(e.target.value)}
            placeholder="learner@example.com"
            className="max-w-xs"
            data-testid="learn-diag-email-input"
          />
          <Button
            onClick={async () => {
              if (!learnDiagEmail || !learnDiagEmail.includes('@')) {
                toast.error('Enter a valid learner email');
                return;
              }
              setRunning(p => ({ ...p, learnDiag: true }));
              setLearnDiagResult(null);
              try {
                const res = await api.get('/api/access/admin/diagnostics/learn-tab', {
                  params: { user_email: learnDiagEmail },
                });
                setLearnDiagResult(res.data);
              } catch (err) {
                toast.error(err.response?.data?.detail || err.message || 'Diagnostic failed');
              } finally {
                setRunning(p => ({ ...p, learnDiag: false }));
              }
            }}
            disabled={running.learnDiag}
            variant="outline"
            data-testid="learn-diag-run-btn"
          >
            {running.learnDiag ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
            Run diagnostic
          </Button>
        </div>

        {learnDiagResult && (
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3 text-sm" data-testid="learn-diag-output">
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-xs">
                <span className="text-muted-foreground">Learner:</span>{' '}
                <span className="font-mono">{learnDiagResult.user?.email}</span>
              </div>
              <div className="text-xs">
                <span className="text-muted-foreground">Stage:</span>{' '}
                <Badge variant="outline">{learnDiagResult.user?.stage || '—'}</Badge>
              </div>
              <div className="text-xs">
                <span className="text-muted-foreground">Tier:</span>{' '}
                <Badge variant="outline">{learnDiagResult.user?.learner_type || '—'}</Badge>
              </div>
              <div className="text-xs">
                <span className="text-muted-foreground">Computed audiences:</span>{' '}
                <code className="bg-background px-1 rounded">{(learnDiagResult.computed_audiences || []).join(', ') || '(none)'}</code>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="default" className="bg-emerald-500">
                Visible: {learnDiagResult.summary?.visible_to_user}
              </Badge>
              <Badge variant="destructive">
                Hidden: {learnDiagResult.summary?.hidden_from_user}
              </Badge>
              <Badge variant="outline">
                Total published: {learnDiagResult.summary?.total_published}
              </Badge>
            </div>

            <div className="rounded bg-background border max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left">Course</th>
                    <th className="px-2 py-1 text-left">Visible</th>
                    <th className="px-2 py-1 text-left">is_listed</th>
                    <th className="px-2 py-1 text-left">Audiences</th>
                    <th className="px-2 py-1 text-left">Listed from / until</th>
                    <th className="px-2 py-1 text-left">Hidden because</th>
                  </tr>
                </thead>
                <tbody>
                  {(learnDiagResult.courses || []).map((c) => (
                    <tr key={c.course_id} className={`border-t ${c.visible ? '' : 'bg-rose-500/5'}`}>
                      <td className="px-2 py-1 truncate max-w-[180px]" title={c.title}>{c.title}</td>
                      <td className="px-2 py-1">
                        {c.visible
                          ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          : <XCircle className="h-4 w-4 text-red-500" />
                        }
                      </td>
                      <td className="px-2 py-1 font-mono">{String(c.discovery.is_listed)}</td>
                      <td className="px-2 py-1 truncate max-w-[120px]" title={(c.discovery.listed_in_audiences || []).join(', ')}>
                        {(c.discovery.listed_in_audiences || []).join(', ') || '(any)'}
                      </td>
                      <td className="px-2 py-1 text-[10px] font-mono">
                        {c.discovery.listed_from ? c.discovery.listed_from.slice(0, 10) : '∞'}
                        {' → '}
                        {c.discovery.listed_until ? c.discovery.listed_until.slice(0, 10) : '∞'}
                      </td>
                      <td className="px-2 py-1 text-[10px] text-red-700">
                        {(c.reasons_hidden || []).join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // V14.5 Part 19q — Postnatal delivery_mode audit + remediation card.
  // Wraps `scripts/v19q_audit_and_migrate_delivery_mode.py` so the founder
  // can run it from this page without shell access.
  if (loading && !data) {
    return (
      <div className="space-y-6 p-6 max-w-5xl" data-testid="launch-readiness-page">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Rocket className="h-8 w-8 text-primary" />
          Launch Readiness
        </h1>
        <DuplicateAccountsCard />
        {renderEmailDiagCard()}
        {renderLearnTabDiagCard()}
        {renderPerfProbeCard()}
        <div className="flex items-center justify-center h-32" data-testid="launch-readiness-loading">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground text-sm">Loading checks…</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6 p-6 max-w-5xl" data-testid="launch-readiness-page">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Rocket className="h-8 w-8 text-primary" />
          Launch Readiness
        </h1>
        <DuplicateAccountsCard />
        {renderEmailDiagCard()}
        {renderLearnTabDiagCard()}
        {renderPerfProbeCard()}
        {/* V14.5 Part 19z14 — Founder bug (2026-06-01): backfill cards
            were only mounted in the success branch (line 1006), so when
            /api/admin/ops/launch-readiness errored on production, the
            entire bottom half — INCLUDING the "Backfill Instructor
            Payouts" button needed to recover from the v19z13 fix — was
            hidden. Surface the most critical production-recovery tools
            here too so they're always reachable, regardless of the
            readiness API's health. */}
        {/* V14.5 Part 19z21 — keep only the Enrollment-Dates backfill reachable
            even when the readiness API errors. */}
        <Card className="border-red-500/40 bg-red-500/5">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Launch-readiness checks failed to load. The Email Transport diagnostics
            above are still available.
            <Button onClick={refresh} variant="outline" size="sm" className="ml-3">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overallOk = data.ok;

  return (
    <div className="space-y-6 p-6 max-w-5xl" data-testid="launch-readiness-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Rocket className="h-8 w-8 text-primary" />
            Launch Readiness
          </h1>
          <p className="text-muted-foreground mt-1">
            One-click production health checks. Click the buttons below to fix the items in red.
          </p>
        </div>
        <Button
          onClick={refresh}
          variant="outline"
          disabled={loading}
          data-testid="refresh-button"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh
        </Button>
      </div>

      {/* Overall status banner */}
      <Card className={overallOk ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-red-500/50 bg-red-500/5'}>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            {overallOk
              ? <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              : <AlertCircle className="h-6 w-6 text-red-500" />
            }
            <div>
              <p className="font-semibold text-lg">
                {overallOk ? 'All systems green — production-ready' : 'Action required before launch'}
              </p>
              {!overallOk && (
                <p className="text-sm text-muted-foreground">
                  {data.recommendations?.length || 0} item(s) need attention
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* V63.7 — kept at the top: this is the tool the team reaches for most often
          (one mother must never sit in the system twice), and it was unfindable
          half-way down a 20-card page. */}
      <DuplicateAccountsCard />

      {renderEmailDiagCard()}


      {renderLearnTabDiagCard()}

      {renderPerfProbeCard()}

      <SlowRequestsCard />

      <PushStoreReadinessCard />

      <StoreReviewAccountCard />



      {/* §1 Environment Variables */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Production Environment Variables
          </CardTitle>
          <CardDescription>
            Set on prod via <span className="font-medium">Emergent Dashboard → Deploy → Environment Variables</span>.
            <br />
            <span className="text-amber-600 font-medium">
              ⚠️ Env vars cannot be set from this page — they're managed by Emergent's deploy infrastructure.
            </span>
            {' '}This page tells you exactly what's missing so you can paste it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(data.env || {}).map(([slot, info]) => {
            const envName = slot.toUpperCase();
            return (
              <div key={slot} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                <StatusIcon ok={info.ok} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="font-semibold">{envName}</code>
                    {info.required && <Badge variant="outline" className="text-xs">REQUIRED</Badge>}
                    {info.ok && <Badge className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30">SET ({info.length} chars)</Badge>}
                    {!info.ok && info.required && <Badge variant="destructive" className="text-xs">MISSING</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{info.message}</p>
                  {info.ok && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Preview: <code className="text-xs bg-muted px-1 py-0.5 rounded">{info.preview}</code>
                    </p>
                  )}
                </div>
              </div>
            );
          })}

          <Separator />

          <details className="text-sm">
            <summary className="cursor-pointer font-medium hover:text-primary">
              📋 Show full instructions for setting env vars on Emergent
            </summary>
            <div className="mt-3 p-4 rounded-lg bg-muted/50 space-y-2 text-sm">
              <p className="font-medium">Steps:</p>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Open <span className="font-medium text-foreground">Emergent Dashboard</span> → your LMS project → <span className="font-medium text-foreground">Deploy</span> tab</li>
                <li>Click <span className="font-medium text-foreground">Environment Variables</span></li>
                <li>For each variable above flagged red, click <span className="font-medium text-foreground">Add Variable</span> and paste the canonical value</li>
                <li>Click <span className="font-medium text-foreground">Save</span></li>
                <li>Click <span className="font-medium text-foreground">Redeploy</span> (~60s rebuild)</li>
                <li>Come back to this page and click <span className="font-medium text-foreground">Refresh</span> — all dots should be green</li>
              </ol>
              <p className="font-medium mt-3">Canonical values (from `LMS_REPLY_TO_APP_V12_FINAL.md` §1):</p>
              <p className="text-xs text-muted-foreground">If you've lost them, the values are also in the latest LMS reply doc shared with App + OPS teams.</p>
            </div>
          </details>
        </CardContent>
      </Card>

      {/* §1.5 Test OPS connection — diagnostic for HMAC mismatches */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Test OPS Connection
          </CardTitle>
          <CardDescription>
            Fires a real signed call from LMS to OPS. Use this when payments return 502 OPS-401 to find the exact mismatch.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={() => runAction('ops_diag',
              async () => {
                const res = await api.post('/api/admin/ops/test-ops-connection');
                setOpsDiag(res.data);
                return res;
              },
              'Diagnostic complete')}
            disabled={running.ops_diag}
            data-testid="test-ops-button"
          >
            {running.ops_diag ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Test OPS Connection
          </Button>

          {opsDiag && (
            <div className={`rounded-lg border p-4 space-y-3 ${opsDiag.ok ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5'}`} data-testid="ops-diag-result">
              <div className="flex items-center gap-2">
                <StatusIcon ok={opsDiag.ok} />
                <span className="font-semibold">
                  OPS responded {opsDiag.ops_response?.status_code || 'unreachable'}
                </span>
              </div>
              {opsDiag.interpretation && (
                <pre className="text-xs whitespace-pre-wrap bg-background/50 p-3 rounded border">
                  {opsDiag.interpretation}
                </pre>
              )}
              <details className="text-xs">
                <summary className="cursor-pointer font-medium">Show full diagnostic data</summary>
                <pre className="mt-2 p-3 rounded bg-muted/50 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(opsDiag, null, 2)}
                </pre>
              </details>
              {!opsDiag.ok && opsDiag.lms_side && (
                <div className="text-xs">
                  <p className="font-semibold mb-1">📋 Send this to OPS team:</p>
                  <pre className="p-3 rounded bg-muted/50 overflow-x-auto whitespace-pre">
LMS prod is signing outbound with:
- HMAC secret preview: {opsDiag.lms_side.outbound_hmac_secret.preview} (length={opsDiag.lms_side.outbound_hmac_secret.length})
- Service key preview: {opsDiag.lms_side.outbound_service_key.preview} (length={opsDiag.lms_side.outbound_service_key.length})

OPS rejected with: {opsDiag.ops_response.status_code} {opsDiag.ops_response.body || ''}

Please verify OPS prod's accept-list includes a matching value for the secret preview above.
                  </pre>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>


      {/* ════════════════════════════════════════════════════════════════
          ADMIN DATA TOOLS
         ════════════════════════════════════════════════════════════════ */}
      {/* V14.5 Part 19z21 — Founder cleanup (2026-06-05): removed one-time
          migrations already executed in production (Instructor Payouts,
          Learner-Attendance re-link) and the Zoom routing / Zoom webhook /
          course-encoding recovery tools. Kept "Backfill Enrollment Dates"
          because new OPS enrollments still need a stamp. */}
      {/* V14.5 Part 17c — Removed one-shot V14.5 launch bulk-assign mount.
          That migration has been executed in production (status.completed=true);
          re-running it 5+ months later is no longer operationally meaningful
          and the component's mount fires an extra /bulk-assign-launch/status
          API call on every page open. Component definition kept in git
          history for traceability; can be re-introduced if a similar
          launch cohort ever needs another bulk-assign. */}

      {/* ════════════════════════════════════════════════════════════════
          AD-HOC OPS — recurring operational tools
         ════════════════════════════════════════════════════════════════ */}

      {/* V25 — post-deploy country & pricing smoke (APP/OPS handover) */}
      <V25SmokeCard />

      {/* V14.7 — Bunny Recording Pipeline Tools */}
      <BunnyPipelineTools running={running} setRunning={setRunning} />

      {/* OPS Pull (V13.14.6 F1) — bulk fetch payments from OPS by email */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-500" />
            Bulk Pull Payments from OPS
          </CardTitle>
          <CardDescription>
            Calls the OPS{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">GET /lms/users/payments</code>{' '}
            endpoint for every learner and mirrors any payments OPS has captured but LMS doesn't
            yet know about (admin-recorded enrolments, manual Razorpay entries, refunded-and-recaptured,
            etc.). Idempotent — safe to run repeatedly.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-lg border bg-blue-50/40 p-3 text-xs space-y-1">
            <p className="font-medium text-blue-900">When to run this</p>
            <ul className="list-disc list-inside text-blue-800 space-y-0.5">
              <li>To backfill payments for already-enrolled learners (Rhea, Nikitha, etc.)</li>
              <li>Periodically (e.g. weekly) to catch any webhook misses</li>
            </ul>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (!window.confirm('Pull payments from OPS for ALL Elite learners? This calls the OPS API for each one.')) return;
                runAction('ops_pull_elite',
                  async () => {
                    const res = await api.post('/api/payment/admin/payments/sync-all-from-ops?learner_type=elite');
                    const d = res.data;
                    toast.success(`OPS pull complete · ${d.users_processed} users · ${d.total_inserted} new payments · ${d.users_with_new_payments} learners updated`, { duration: 7000 });
                    return res;
                  },
                  null);
              }}
              disabled={running.ops_pull_elite || running.ops_pull_all}
              data-testid="ops-pull-elite-button"
            >
              {running.ops_pull_elite ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Pull for Elite Learners only
            </Button>
            <Button
              onClick={() => {
                if (!window.confirm('Pull payments from OPS for ALL learners? This may take a few minutes for large user bases.')) return;
                runAction('ops_pull_all',
                  async () => {
                    const res = await api.post('/api/payment/admin/payments/sync-all-from-ops?role=learner');
                    const d = res.data;
                    toast.success(`OPS pull complete · ${d.users_processed} users · ${d.total_inserted} new payments · ${d.users_with_new_payments} learners updated`, { duration: 7000 });
                    return res;
                  },
                  null);
              }}
              disabled={running.ops_pull_all || running.ops_pull_elite}
              data-testid="ops-pull-all-button"
            >
              {running.ops_pull_all ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Pull for ALL Learners
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Runs sequentially to be polite to OPS. Per-user errors are aggregated and surfaced in the response —
            no single failure aborts the batch.
          </p>
        </CardContent>
      </Card>

      {/* FCM test moved to /admin/test-push */}

      {/* ════════════════════════════════════════════════════════════════
          RECOMMENDATIONS — dynamic, derived from backend health checks
         ════════════════════════════════════════════════════════════════ */}
      {data.recommendations && data.recommendations.length > 0 && (
        <Card className={overallOk ? 'border-emerald-500/30' : 'border-amber-500/40 bg-amber-500/5'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {overallOk ? 'All Clear' : 'Action Items'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className="text-muted-foreground">•</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


function CourseEncodingReconcile() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (dryRun) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('dry_run', dryRun ? 'true' : 'false');
      params.set('include_ready', 'true');
      const res = await api.post(`/api/courses/admin/reconcile-encoding-bulk?${params.toString()}`);
      setResult(res.data);
      const sum = res.data.summary || {};
      toast.success(
        dryRun
          ? `Dry-run: scanned ${sum.episodes_checked_against_bunny} eps across ${sum.courses_scanned} courses · ${sum.would_fix} would change`
          : `Fixed ${sum.fixed_now} episodes across ${sum.courses_scanned} courses`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="course-encoding-reconcile-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Course Encoding Reconcile (V14.5)
        </CardTitle>
        <CardDescription>
          Scans every course's episodes against Bunny. Catches episodes wrongly
          flipped to <strong>ready</strong> by the pre-V14.5 off-by-one bug
          (Bunny errors 5/6 silently matched <code>{'>='}4</code>) and
          reclassifies them to <strong>failed</strong>. Also picks up any
          stuck-encoding episodes whose webhook missed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2 flex-wrap">
          <Button onClick={() => run(true)} disabled={loading} variant="outline" data-testid="encoding-dryrun-btn">
            {loading ? 'Scanning…' : '1. Dry-run scan'}
          </Button>
          <Button
            onClick={() => {
              if (window.confirm('This will reclassify episodes across ALL courses. Run dry-run first if unsure. Continue?')) {
                run(false);
              }
            }}
            disabled={loading}
            variant="destructive"
            data-testid="encoding-apply-btn"
          >
            2. Apply fix
          </Button>
        </div>

        {result && (
          <div className="border rounded-xl p-3 space-y-2" data-testid="encoding-result">
            <p className="text-sm">
              <strong>{result.dry_run ? 'Dry run' : 'Applied'}:</strong>{' '}
              {result.summary?.courses_scanned} courses ·{' '}
              {result.summary?.episodes_checked_against_bunny} episodes checked ·{' '}
              {result.dry_run ? `${result.summary?.would_fix} would change` : `${result.summary?.fixed_now} fixed`}
              {result.summary?.bunny_unreachable > 0 && (
                <> · <span className="text-amber-700">{result.summary.bunny_unreachable} unreachable</span></>
              )}
            </p>
            {(result.rows || []).length > 0 ? (
              <div className="max-h-72 overflow-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1">Course</th>
                      <th>Episode</th>
                      <th>Bunny status</th>
                      <th>Was</th>
                      <th>Now</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={`${r.course_id}-${r.episode_id}`}>
                        <td className="py-1 max-w-[140px] truncate" title={r.course_title}>{r.course_title}</td>
                        <td className="max-w-[140px] truncate" title={r.episode_title}>{r.episode_title}</td>
                        <td>{r.bunny_status}</td>
                        <td><span className="text-slate-600">{r.from}</span></td>
                        <td>
                          {r.to === 'failed'
                            ? <span className="text-rose-700">{r.to}</span>
                            : r.to === 'ready'
                              ? <span className="text-emerald-700">{r.to}</span>
                              : r.to}
                        </td>
                        <td className="max-w-[160px] truncate" title={r.bunny_error || ''}>{r.bunny_error || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No episodes need reclassification — all course encoding states match Bunny reality.</p>
            )}
            {(result.bunny_unreachable || []).length > 0 && (
              <details className="mt-2">
                <summary className="text-xs text-amber-700 cursor-pointer">⚠ {result.bunny_unreachable.length} episode(s) had Bunny API errors</summary>
                <ul className="text-xs mt-1 space-y-0.5">
                  {result.bunny_unreachable.slice(0, 10).map((u, i) => (
                    <li key={i}>{u.course_title} — {u.episode_title}: <span className="text-muted-foreground">{u.error}</span></li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ZoomWebhookRecovery() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [batchId, setBatchId] = useState('');

  const run = async (dryRun) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('dry_run', dryRun ? 'true' : 'false');
      if (batchId.trim()) params.set('batch_id', batchId.trim());
      const res = await api.post(`/api/sessions/zoom/revive-wrongly-completed?${params.toString()}`);
      setResult(res.data);
      const sum = res.data.summary || {};
      toast.success(
        dryRun
          ? `Dry-run: scanned ${sum.scanned}, ${sum.revived} would be revived, ${sum.skipped} skipped`
          : `Revived ${sum.revived} session(s), skipped ${sum.skipped}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revive failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="zoom-webhook-recovery-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🛟 Zoom Webhook Recovery (V14.4)
        </CardTitle>
        <CardDescription>
          Revive sessions wrongly marked <strong>completed</strong> by a spurious Zoom <code>meeting.ended</code> webhook.
          Verifies via Zoom REST + live participants + recent attendance before reviving.
          Only considers sessions inside scheduled window + 30 min grace.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-muted-foreground" htmlFor="recovery-batch-id">
              Optional: limit to a batch_id
            </label>
            <Input
              id="recovery-batch-id"
              placeholder="leave blank to scan all"
              value={batchId}
              onChange={(e) => setBatchId(e.target.value)}
              data-testid="recovery-batch-id-input"
            />
          </div>
          <Button onClick={() => run(true)} disabled={loading} variant="outline" data-testid="recovery-dryrun-btn">
            {loading ? 'Scanning…' : '1. Dry-run scan'}
          </Button>
          <Button
            onClick={() => {
              if (window.confirm('This will flip completed → live for sessions Zoom confirms are still alive. Continue?')) {
                run(false);
              }
            }}
            disabled={loading}
            variant="destructive"
            data-testid="recovery-apply-btn"
          >
            2. Apply revive
          </Button>
        </div>

        {result && (
          <div className="border rounded-xl p-3 space-y-2" data-testid="recovery-result">
            <p className="text-sm">
              <strong>{result.dry_run ? 'Dry run' : 'Applied'}:</strong>{' '}
              scanned {result.summary?.scanned}, revived {result.summary?.revived}, skipped {result.summary?.skipped}
            </p>
            {(result.rows || []).length > 0 ? (
              <div className="max-h-72 overflow-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1">Date</th>
                      <th>Time</th>
                      <th>Title</th>
                      <th>Zoom status</th>
                      <th>Participants</th>
                      <th>Recent attend</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r) => (
                      <tr key={r.session_id}>
                        <td className="py-1">{r.scheduled_date || '—'}</td>
                        <td>{r.scheduled_time || '—'}</td>
                        <td className="max-w-[160px] truncate">{r.title || '—'}</td>
                        <td>{r.zoom_status ?? '—'}</td>
                        <td>{r.live_participants ?? '—'}</td>
                        <td>{r.recent_attendance ? '✓' : '—'}</td>
                        <td>
                          {r.action === 'revived' && <span className="text-emerald-700">✅ revived</span>}
                          {r.action === 'would_revive' && <span className="text-amber-700">↪ would revive</span>}
                          {r.action === 'skip_dead' && <span className="text-slate-600" title={r.reason}>⏸ no live signal</span>}
                          {r.action === 'skip_past_grace' && <span className="text-slate-600" title={r.reason}>⏸ past grace</span>}
                          {r.action === 'skip_race' && <span className="text-slate-600" title={r.reason}>⏸ race</span>}
                          {r.action === 'skip_date_parse_failed' && <span className="text-rose-700" title={r.reason}>❌ parse error</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No completed sessions matched today + yesterday.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function ZoomRoutingDiagnostics() {
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [repair, setRepair] = useState(null);
  const [repairLoading, setRepairLoading] = useState(false);

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await api.get('/api/zoom/license-audit');
      setAudit(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'License audit failed');
    } finally {
      setAuditLoading(false);
    }
  };

  const runRepair = async (dryRun) => {
    setRepairLoading(true);
    try {
      const res = await api.post(`/api/sessions/zoom/repair-misrouted?dry_run=${dryRun}`);
      setRepair(res.data);
      toast.success(
        dryRun
          ? `Dry-run: ${res.data.summary?.scanned} mis-routed sessions found`
          : `Re-hosted ${res.data.summary?.fixed} session(s)`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairLoading(false);
    }
  };

  return (
    <Card data-testid="zoom-routing-diagnostics-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          🎥 Zoom Routing Diagnostics (V14.3)
        </CardTitle>
        <CardDescription>
          Catch parallel-class collision bugs before they hit production. Run before every busy class day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={runAudit} disabled={auditLoading} data-testid="zoom-license-audit-btn">
            {auditLoading ? 'Auditing…' : '1. Audit instructor licenses'}
          </Button>
          <Button onClick={() => runRepair(true)} disabled={repairLoading} variant="outline" data-testid="zoom-repair-dryrun-btn">
            {repairLoading ? 'Scanning…' : '2. Preview mis-routed sessions (dry run)'}
          </Button>
          <Button
            onClick={() => {
              if (window.confirm('This will delete + recreate Zoom meetings for every mis-routed upcoming session. Continue?')) {
                runRepair(false);
              }
            }}
            disabled={repairLoading}
            variant="destructive"
            data-testid="zoom-repair-apply-btn"
          >
            3. Repair mis-routed sessions
          </Button>
        </div>

        {audit && (
          <div className="border rounded-xl p-3 space-y-2" data-testid="audit-result">
            <p className="text-sm">
              <strong>Summary:</strong>{' '}
              {audit.summary.total_instructors} instructors —{' '}
              <span className="text-emerald-700">{audit.summary.licensed} licensed</span>,{' '}
              <span className="text-rose-700">{audit.summary.unlicensed} unlicensed</span>
              {audit.summary.would_route_to_pool > 0 && (
                <span className="text-amber-700"> · ⚠ {audit.summary.would_route_to_pool} would route to pool</span>
              )}
            </p>
            <div className="max-h-72 overflow-auto">
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1">Name</th>
                    <th>Email</th>
                    <th>Zoom API</th>
                    <th>Mirror</th>
                    <th>Licensed</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.rows.map((r) => (
                    <tr key={r.user_id} className={r.is_licensed ? '' : 'bg-rose-50 dark:bg-rose-950/20'}>
                      <td className="py-1">{r.name}</td>
                      <td className="font-mono text-[10px]">{r.email}</td>
                      <td>{r.in_zoom_api ? `✓ type=${r.zoom_type}` : '—'}</td>
                      <td>{r.in_zoom_licenses_col ? '✓' : '—'}</td>
                      <td>{r.is_licensed ? '✅' : '❌ POOL'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {repair && (
          <div className="border rounded-xl p-3 space-y-2" data-testid="repair-result">
            <p className="text-sm">
              <strong>{repair.dry_run ? 'Dry run' : 'Repair'}:</strong>{' '}
              scanned {repair.summary.scanned}, fixed {repair.summary.fixed},
              skipped {repair.summary.skipped_unlicensed} unlicensed,
              skipped {repair.summary.skipped_in_progress} in-progress
            </p>
            {repair.rows.length > 0 && (
              <div className="max-h-72 overflow-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1">Date</th>
                      <th>Time</th>
                      <th>Title</th>
                      <th>Instructor</th>
                      <th>Old Host</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repair.rows.map((r) => (
                      <tr key={r.session_id}>
                        <td className="py-1">{r.scheduled_date}</td>
                        <td>{r.scheduled_time}</td>
                        <td className="max-w-[160px] truncate">{r.title}</td>
                        <td className="font-mono text-[10px]">{r.instructor_email}</td>
                        <td className="font-mono text-[10px]">{r.current_host_email}</td>
                        <td>
                          {r.action === 'rehosted' && (
                            <span className="text-emerald-700">
                              ✅ rehosted → {r.new_host_email}
                            </span>
                          )}
                          {r.action === 'would_rehost' && <span className="text-amber-700">↪ would rehost</span>}
                          {r.action === 'skip_unlicensed' && (
                            <span className="text-rose-700" title={r.reason}>⚠ unlicensed</span>
                          )}
                          {r.action === 'skip_in_progress' && <span className="text-slate-600">⏸ in-progress</span>}
                          {(r.action === 'rehost_failed' || r.action === 'error') && (
                            <span
                              className="text-rose-700 cursor-help"
                              title={r.reason || 'Unknown failure'}
                            >
                              ❌ {r.action === 'rehost_failed' ? 'failed' : 'error'} — {(r.reason || '').slice(0, 80)}
                              {r.reason && r.reason.length > 80 ? '…' : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// V14.5 Part 17c — V14_5LaunchBulkAssign component removed. Migration
// completed in production; component preserved in git history.



// V14.5 Part 17d — Production data-fix card #1: Backfill instructor payouts.
// Walks every COMPLETED + MISSED session and creates pending payout-adjustment
// rows for any session that's missing one. Idempotent.


// V14.5 Part 17d — Production data-fix card #2: Backfill learner attendance.
// Phase 1 (always-on): re-links existing attendance entries with lms_user_id=null
// by email/CI → join_intent fuzzy → booked-learner name fuzzy.
// Phase 2 (opt-in): pulls definitive attendance from Zoom's /past_meetings API.

// V14.5 Part 19z18 — Production data-fix card #3: Backfill learner enrollment
// stamps. OPS / bulk-enroll paths never wrote `learner_assignments[].assigned_at`,
// so attendance % fell back to batch.created_at and under-counted OPS-enrolled
// learners (the Aneeta Mahan 47% bug). This stamps each (batch, learner) pair
// missing one with the later of batch.created_at / user.created_at — idempotent.


// V14.5 Part 19z21 — Recompute & snapshot every learner's attendance % using
// the new founder formula (classes attended ÷ classes booked). Attendance is
// computed live on read, so this is a verification/audit pass that writes a
// durable snapshot onto each learner so the correction is visible.
